import { Badge, Card, CardContent, CardHeader, LoadingSpinner, Typography } from '@pesto/ui';
import { formatDate } from '@pesto/utils';
import { ChevronRight, Sparkles, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { SortDirection, useListUsersQuery } from '../../../__generated__/graphql/api';
import { USERS_DEFAULT_FILTERS } from '../../../constants/defaultListFilters';
import { routes } from '../../../constants/routes';

const buildDisplayName = (firstName: string | null | undefined, lastName: string | null | undefined) => {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();

  return name === '' ? null : name;
};

export const RecentUsersCard = () => {
  const navigate = useNavigate();

  const { data, loading, error } = useListUsersQuery({
    variables: {
      ...USERS_DEFAULT_FILTERS,
      insertedAtSortDirection: SortDirection.Desc,
      resultsPerPage: 5,
      pageNumber: 1,
    },
    notifyOnNetworkStatusChange: true,
  });

  const users = (data?.listUsers?.users ?? []).filter((u): u is NonNullable<typeof u> => Boolean(u));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <Typography variant="headerLarge" className="text-base font-semibold">
            Recent users
          </Typography>
          <Typography variant="caption" className="text-muted-foreground">
            5 most recently added
          </Typography>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {error ? (
          <div className="px-6 py-8">
            <Typography variant="body" className="text-destructive">
              We couldn't load recent users. {error.message}
            </Typography>
          </div>
        ) : loading && users.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : users.length === 0 ? (
          <div className="px-6 py-8">
            <Typography variant="body" className="text-muted-foreground">
              No users yet.
            </Typography>
          </div>
        ) : (
          <ul className="divide-border/60 divide-y">
            {users.map((user) => {
              const displayName = buildDisplayName(user.firstName, user.lastName) ?? '—';

              return (
                <li key={user.id ?? displayName}>
                  <button
                    type="button"
                    onClick={() => user.id && navigate(routes.users + '/' + user.id)}
                    className="hover:bg-muted/50 focus-visible:bg-muted/50 group flex w-full items-center gap-4 px-6 py-3 text-left transition-colors focus-visible:outline-none"
                  >
                    <div className="bg-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
                      <User className="text-muted-foreground h-4 w-4" />
                    </div>
                    <div className="flex-1 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Typography variant="body" className="font-medium">
                          {displayName}
                        </Typography>
                        {user.isFriendsAndFam && (
                          <Badge variant="outline" className="text-xs">
                            <Sparkles className="mr-1 h-3 w-3" />
                            F&amp;F
                          </Badge>
                        )}
                      </div>
                      <Typography variant="caption" className="text-muted-foreground">
                        {user.email ?? '—'}
                        {user.insertedAt && ` · added ${formatDate(user.insertedAt)}`}
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground/60 group-hover:text-muted-foreground h-4 w-4 transition-colors" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
